import { useRecoilState, useResetRecoilState } from "recoil";
import { useQueryClient } from "@tanstack/react-query";
import {REFRESHTOKEN_KEY, ACCESSTOKEN_KEY} from "configs/api";
import { getOrStartRefresh } from "services/refreshManager";

//account atoms
import authAtom from "recoil/account/auth";
import bankDetailsAtom from "recoil/account/bankDetails";
import billingInfoAtom from "recoil/account/billingInfo";
import paymentInfoAtom from "recoil/account/paymentInfo";
import phoneDetailsAtom from "recoil/account/phoneDetails";
import profilesAtom from "recoil/account/profiles";
import promoteChannelsAtom from "recoil/account/promoteChannels";
import regStepAtom from "recoil/account/regStep";
import rhpLinkAccountAtom from "recoil/account/rhpLinkAccount";

//nav atoms
import leftMenuAtom from "recoil/nav/leftMenu";

//stats atoms:
import campaignDateRangeIDAtom from "recoil/stats/campaignDateRangeID";
import { dashboardCampaignSelectionAtom, dashboardSelectedMonthAtom } from "recoil/stats/dashboardFilters";

//query keys
import { ACCOUNT_KEY } from "hooks/useAccountData";
import { RHP_EVENTS_KEY } from "hooks/useRHPEvents";
import { RHP_GROUPS_KEY } from "hooks/useRHPGroups";
import { TOTAL_EARNINGS_KEY } from "hooks/useTotalEarnings";
import { EARNINGS_KEY_PREFIX } from "hooks/useEarnings";
import { DAILY_BREAKDOWN_KEY_PREFIX } from "hooks/useDailyBreakdown";
import { CAMPAIGNS_KEY_PREFIX } from "hooks/useCampaignsSummary";
import { CAMPAIGN_DETAIL_KEY_PREFIX } from "hooks/useCampaignDetail";
import { REFERRAL_KEY_PREFIX } from "./useReferral";

//lookup atoms:
import lookupLandingPagesAtom from "recoil/lookup/landingPages";

const useAuthProvider = () => {
    const queryClient = useQueryClient();
    const [authState, setAuthState] = useRecoilState(authAtom);
    const resetAuth = useResetRecoilState(authAtom);
    const resetBankDetailsAtom = useResetRecoilState(bankDetailsAtom);
    const resetBillingInfoAtom = useResetRecoilState(billingInfoAtom);
    const resetPaymentInfoAtom = useResetRecoilState(paymentInfoAtom);
    const resetPhoneDetailsAtom = useResetRecoilState(phoneDetailsAtom);
    const resetProfilesAtom = useResetRecoilState(profilesAtom);
    const resetPromoteChannelsAtom = useResetRecoilState(promoteChannelsAtom);
    const resetRegStepAtom = useResetRecoilState(regStepAtom);
    const resetLeftMenuAtom = useResetRecoilState(leftMenuAtom);
    const resetCampaignDateRangeIDAtom = useResetRecoilState(campaignDateRangeIDAtom);
    const resetLookupLandingPagesAtom = useResetRecoilState(lookupLandingPagesAtom);
    const resetDashboardCampaignSelection = useResetRecoilState(dashboardCampaignSelectionAtom);
    const resetDashboardSelectedMonth = useResetRecoilState(dashboardSelectedMonthAtom);
    const resetRHPLinkAccountAtom = useResetRecoilState(rhpLinkAccountAtom);

    const clearTokens = ()=>{
        localStorage.removeItem(ACCESSTOKEN_KEY);
        localStorage.removeItem(REFRESHTOKEN_KEY);

        //set auth atom to not authenticated
        //setAuthState({...authState, isAuthenticated: false});
        resetAuth();
        resetBankDetailsAtom();
        resetBillingInfoAtom();
        resetPaymentInfoAtom();
        resetPhoneDetailsAtom();
        resetProfilesAtom();
        resetPromoteChannelsAtom();
        resetRegStepAtom();
        resetLeftMenuAtom();
        resetCampaignDateRangeIDAtom();
        queryClient.removeQueries(ACCOUNT_KEY);
        queryClient.removeQueries(RHP_EVENTS_KEY);
        queryClient.removeQueries(RHP_GROUPS_KEY);
        queryClient.removeQueries(TOTAL_EARNINGS_KEY);
        queryClient.removeQueries(EARNINGS_KEY_PREFIX);
        queryClient.removeQueries(CAMPAIGNS_KEY_PREFIX);
        queryClient.removeQueries(CAMPAIGN_DETAIL_KEY_PREFIX);
        resetLookupLandingPagesAtom();
        resetDashboardCampaignSelection();
        resetDashboardSelectedMonth();
        queryClient.removeQueries(DAILY_BREAKDOWN_KEY_PREFIX);
        queryClient.removeQueries(REFERRAL_KEY_PREFIX);
        resetRHPLinkAccountAtom();
        //setAuthState({...authState, isAuthenticated: false, accountLoaded:false});
    };


    const getBearerToken = () => {
        return localStorage.getItem(ACCESSTOKEN_KEY);
    };

    const getRefreshToken = () => {
        return localStorage.getItem(REFRESHTOKEN_KEY);
    };

    const setAuthTokens = ({BearerToken, RefreshToken}) => {
        //store tokens
        localStorage.setItem(ACCESSTOKEN_KEY, BearerToken);
        localStorage.setItem(REFRESHTOKEN_KEY, RefreshToken);

        //set auth atom to authenticated
        setAuthState(prev=>(
             {...prev, isAuthenticated:  !!(BearerToken && BearerToken !== 'undefined' &&
                          RefreshToken && RefreshToken !== 'undefined')}
        ));

    }

    const getNewAccessToken = async (failedRequest) => {
        const accessToken = await getOrStartRefresh();
        failedRequest.headers = { ...failedRequest.headers, Authorization: `Bearer ${accessToken}` };
        return accessToken;
    };

    return {
        ...authState,
        clearTokens,
        getBearerToken,
        getNewAccessToken,
        setAuthTokens,
        isAuthenticated : authState.isAuthenticated
    };
}

export default useAuthProvider;